import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {GIZStoreModule} from "./shared/store/store.module";
import {HTTP_INTERCEPTORS, HttpBackend, HttpClientModule} from "@angular/common/http";
import {GlobalErrorHandler, LoadingInterceptor} from "./shared/classes";
import {FileManagerControllerComponent} from "./shared/components";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {translationLocations} from "../assets/config/config";
import {MultiTranslateHttpLoader} from "ngx-translate-multi-http-loader";
import {ClickOutsideDirective} from "./directives/shared/click-outside.directive";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {MaterialModule} from "./material.module";
import {environment} from "../environments/environment";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from "@azure/msal-browser";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalRedirectComponent,
  MsalService
} from "@azure/msal-angular";
import {ToastrModule} from "ngx-toastr";
import {SharedModule} from "./shared/shared.module";
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AgModule} from "./ag.module";
import {MockMsalGuard} from "./local-dev/mock-msal.guard";
import {LocalAuthInterceptor} from "./local-dev/local-auth.interceptor";

export function HttpLoaderFactory(_httpBackend: HttpBackend) {
  const fileLocations: {prefix: string, suffix: string}[] = [];
  translationLocations.forEach(location => {
    fileLocations.push({prefix: location, suffix: '.json'})
  })
  return new MultiTranslateHttpLoader(_httpBackend, fileLocations);
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiUrl, environment.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.scopes]
    },
    loginFailedRoute: '/'
  };
}

// MSAL v3 requires the instance to finish initializing before MsalGuard/MsalInterceptor run;
// otherwise canActivate can hit "uninitialized_public_client_application" and silently fail to redirect.
export function initializeMsal(msalService: MsalService): () => Promise<void> {
  return () => msalService.instance.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    ClickOutsideDirective,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en-GB',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      }
    }),
    AppRoutingModule,
    GIZStoreModule,
    FormsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    MaterialModule,
    AgModule,
    SharedModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    NgbModule
  ],
  providers: [
    {provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true},
    ...(environment.useMockAuth
      ? [
          {provide: HTTP_INTERCEPTORS, useClass: LocalAuthInterceptor, multi: true},
          {provide: MsalGuard, useClass: MockMsalGuard}
        ]
      : [
          {provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true},
          MsalGuard
        ]),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true
    },
    MsalService,
    MsalBroadcastService
  ],
  exports: [
    FileManagerControllerComponent
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
